import React, { useEffect, useState } from 'react';
import { ClipboardList, ExternalLink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from '@roger/ui-components';
import {
  getAdmissionAdmissionsAdmissionIdGet as getAdmission,
  listEpisodeFrequenciesEpisodesEpisodeIdFrequenciesGet as listEpisodeFrequencies,
} from '../../services/api/sdk.gen';
import type {
  ClinicPhysicianResponse,
  EpisodeSummaryResponse,
  FrequencyResponse,
  InsurancePosition,
  PatientInsuranceResponse,
  PatientResponse,
} from '../../services/api/types.gen';
import {
  EMPLOYMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
} from '../../constants/options';
import { calculateAge, formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { statusDisplayLabel } from '../../utils/episodeStatus';
import { formatPhysicianName } from '../../utils/formatting';
import { buildGoogleMapsUrl } from '../../utils/mapsLink';
import { PhoneLink } from '../PhoneLink';

interface PatientFaceSheetProps {
  patient: PatientResponse;
  /** Episode in context, or null when the patient has none yet. */
  episode: EpisodeSummaryResponse | null;
  /**
   * Physician roster for the patient's clinic, already fetched and cached by
   * the chart. The attending is resolved from it rather than fetched, so the
   * sheet costs no round trip to name a phone number.
   */
  physicians: ClinicPhysicianResponse[];
  insurances: PatientInsuranceResponse[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY = 'N/A';

/** Billing order, so a stacked insurance list reads primary first. */
const INSURANCE_POSITION_ORDER: InsurancePosition[] = [
  'Primary',
  'Secondary',
  'Tertiary',
  'Quaternary',
];

/** Discipline frequencies in the order a care team reads them. */
const DISCIPLINE_FREQUENCIES = [
  { label: 'SN', key: 'sn_frequency' },
  { label: 'PT', key: 'pt_frequency' },
  { label: 'OT', key: 'ot_frequency' },
  { label: 'ST', key: 'st_frequency' },
  { label: 'MSW', key: 'msw_frequency' },
  { label: 'HHA', key: 'hha_frequency' },
] as const satisfies readonly {
  label: string;
  key: keyof FrequencyResponse;
}[];

function labelFor(
  options: readonly { value: string; label: string }[],
  value: string | null | undefined
): string | null {
  if (!value) return null;
  return options.find(option => option.value === value)?.label ?? value;
}

function formatDate(value: string | null | undefined): string | null {
  return formatDateToMMDDYYYY(value) || null;
}

interface RowProps {
  label: string;
  /** When false the row does not render at all, keeping the sheet short. */
  show?: boolean;
  children: React.ReactNode;
}

function Row({ label, show = true, children }: RowProps) {
  if (!show) return null;
  return (
    <div className='flex gap-3 py-1 text-sm'>
      <dt className='w-32 shrink-0 text-muted-foreground'>{label}</dt>
      <dd className='min-w-0 flex-1 break-words font-medium'>{children}</dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className='border-t py-3 first:border-t-0 first:pt-0'>
      <h3 className='mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        {title}
      </h3>
      <dl>{children}</dl>
    </section>
  );
}

/**
 * Read-only patient summary for visit preparation, opened from the chart
 * header.
 *
 * Everything a clinician needs at the door in one scroll: who the patient is,
 * every number worth calling, where the house is, and what the episode
 * authorizes. Phones go through PhoneLink so they dial on a phone, and the
 * address links out to a map. Nothing here is editable; the Patient Info tab
 * remains the place to change any of it.
 *
 * SSN is deliberately absent. It is on the Patient Info tab, but this sheet is
 * meant to be opened on a phone inside a patient's home, and start-of-care
 * prep never needs it.
 */
export const PatientFaceSheet: React.FC<PatientFaceSheetProps> = ({
  patient,
  episode,
  physicians,
  insurances,
  open,
  onOpenChange,
}) => {
  const [socDate, setSocDate] = useState<string | null>(null);
  const [socLoading, setSocLoading] = useState(false);
  const [frequencies, setFrequencies] = useState<FrequencyResponse[]>([]);
  const [frequenciesLoading, setFrequenciesLoading] = useState(false);

  const admissionId = episode?.admission_id ?? null;
  const episodeId = episode?.id ?? null;

  // Start of care lives on the admission, not the episode: `episode.start_date`
  // is the current certification period, which for a recertified patient is
  // months after the patient started care.
  useEffect(() => {
    if (!open || !admissionId) {
      setSocDate(null);
      // Also clear the in-flight flag: the cancelled run's `finally` never
      // lands, so leaving it set strands the skeleton on the next open for an
      // episode that has no admission to fetch.
      setSocLoading(false);
      return;
    }

    let cancelled = false;
    setSocLoading(true);
    (async () => {
      try {
        const response = await getAdmission({
          path: { admission_id: admissionId },
        });
        if (!cancelled) setSocDate(response?.data?.resolved_soc_date ?? null);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load admission for face sheet:', err);
          setSocDate(null);
        }
      } finally {
        if (!cancelled) setSocLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, admissionId]);

  useEffect(() => {
    if (!open || !episodeId) {
      setFrequencies([]);
      setFrequenciesLoading(false);
      return;
    }

    let cancelled = false;
    setFrequenciesLoading(true);
    (async () => {
      try {
        const response = await listEpisodeFrequencies({
          path: { episode_id: episodeId },
        });
        if (!cancelled) setFrequencies(response?.data ?? []);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load episode frequencies:', err);
          setFrequencies([]);
        }
      } finally {
        if (!cancelled) setFrequenciesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, episodeId]);

  const fullName = [
    patient.first_name,
    patient.middle_name,
    patient.last_name,
    patient.suffix,
  ]
    .filter(Boolean)
    .join(' ');

  const age = calculateAge(patient.date_of_birth);
  const dob = formatDate(patient.date_of_birth);
  const mapsUrl = buildGoogleMapsUrl(patient);
  const addressLine = [
    patient.address,
    patient.city,
    [patient.state, patient.zip_code].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  const physicianId = episode?.clinic_physician_id ?? null;
  const physician = physicianId
    ? (physicians.find(candidate => candidate.id === physicianId) ?? null)
    : null;
  // A patient's episode can sit at a different clinic than the patient, and the
  // roster handed in covers only the patient's. The episode still carries the
  // assigned name, so fall back to it rather than claiming nobody is assigned.
  const physicianName = physician
    ? formatPhysicianName(physician)
    : (episode?.clinic_physician_name ?? null);

  // An episode can hold several frequency periods when orders change mid
  // certification. The sheet answers "what am I authorized to do now", so it
  // shows the most recent period and labels its dates.
  const currentFrequency =
    frequencies.length > 0
      ? [...frequencies].sort((a, b) =>
          a.start_date < b.start_date ? 1 : a.start_date > b.start_date ? -1 : 0
        )[0]
      : null;
  const orderedDisciplines = currentFrequency
    ? DISCIPLINE_FREQUENCIES.filter(
        discipline => currentFrequency[discipline.key]
      )
    : [];

  // Primary first: a clinician filling a requisition needs to know which
  // payer to bill before which, and the array arrives in no guaranteed order.
  const orderedInsurances = [...insurances].sort(
    (a, b) =>
      INSURANCE_POSITION_ORDER.indexOf(a.position) -
      INSURANCE_POSITION_ORDER.indexOf(b.position)
  );

  const showLegal = Boolean(
    patient.needs_legal_representative || patient.has_poa
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* w-full below md so a phone gets the whole viewport; sm:max-w-none is
          required because the side variant caps width at sm:max-w-sm, and
          twMerge only resolves a conflict at the same breakpoint. */}
      <SheetContent
        side='right'
        className='flex w-full flex-col sm:max-w-none md:w-[440px]'
      >
        <SheetHeader>
          <SheetTitle className='flex items-center gap-2'>
            <ClipboardList className='h-4 w-4' />
            Patient Summary
          </SheetTitle>
          {/* Radix announces the description alongside the title when the sheet
              opens, and warns when one is absent. Visually hidden: the sections
              below already label themselves on screen. */}
          <SheetDescription className='sr-only'>
            Read-only patient demographics, contacts, insurance, episode and
            physician details.
          </SheetDescription>
        </SheetHeader>

        <div className='mt-2 flex-1 overflow-y-auto'>
          <Section title='Patient'>
            <Row label='Name'>{fullName}</Row>
            <Row label='MRN'>{patient.mrn || EMPTY}</Row>
            <Row label='Date of Birth'>
              {dob ?? EMPTY}
              {age !== null && ` (${age} yrs)`}
            </Row>
            <Row label='Gender' show={Boolean(patient.gender)}>
              {labelFor(GENDER_OPTIONS, patient.gender)}
            </Row>
            <Row label='Marital Status' show={Boolean(patient.marital_status)}>
              {labelFor(MARITAL_STATUS_OPTIONS, patient.marital_status)}
            </Row>
            <Row label='Employment' show={Boolean(patient.employment_status)}>
              {labelFor(EMPLOYMENT_STATUS_OPTIONS, patient.employment_status)}
            </Row>
            <Row label='Language' show={Boolean(patient.primary_language)}>
              {patient.primary_language}
            </Row>
            <Row label='MBI' show={Boolean(patient.mbi)}>
              {patient.mbi}
            </Row>
          </Section>

          <Section title='Contact'>
            <Row label='Phone'>
              <PhoneLink phone={patient.phone} />
            </Row>
            <Row label='Email' show={Boolean(patient.email)}>
              <a
                href={`mailto:${patient.email}`}
                className='text-blue-600 hover:underline'
              >
                {patient.email}
              </a>
            </Row>
            <Row label='Address'>
              {mapsUrl ? (
                <a
                  href={mapsUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-start gap-1 text-blue-600 hover:underline'
                >
                  <span>{addressLine}</span>
                  <ExternalLink className='mt-0.5 h-3 w-3 shrink-0' />
                </a>
              ) : (
                EMPTY
              )}
            </Row>
            <Row label='County' show={Boolean(patient.county)}>
              {patient.county}
            </Row>
          </Section>

          {/* A number with no name attached is still the backup number this
              summary exists to provide, so either half earns the section. */}
          {(patient.emergency_contact || patient.emergency_phone) && (
            <Section title='Emergency Contact'>
              <Row label='Name' show={Boolean(patient.emergency_contact)}>
                {patient.emergency_contact}
              </Row>
              <Row
                label='Relationship'
                show={Boolean(patient.emergency_contact_relationship)}
              >
                {patient.emergency_contact_relationship}
              </Row>
              <Row label='Phone'>
                <PhoneLink phone={patient.emergency_phone} />
              </Row>
            </Section>
          )}

          {(patient.secondary_contact || patient.secondary_phone) && (
            <Section title='Secondary Contact'>
              <Row label='Name' show={Boolean(patient.secondary_contact)}>
                {patient.secondary_contact}
              </Row>
              <Row
                label='Relationship'
                show={Boolean(patient.secondary_contact_relationship)}
              >
                {patient.secondary_contact_relationship}
              </Row>
              <Row label='Phone'>
                <PhoneLink phone={patient.secondary_phone} />
              </Row>
            </Section>
          )}

          {showLegal && (
            <Section title='Representation'>
              <Row
                label='Legal Rep'
                show={Boolean(patient.needs_legal_representative)}
              >
                Needed
              </Row>
              <Row label='POA' show={Boolean(patient.has_poa)}>
                On file
              </Row>
            </Section>
          )}

          {orderedInsurances.map(insurance => (
            <Section
              key={insurance.id}
              title={
                insurance.position
                  ? `${insurance.position} Insurance`
                  : 'Insurance'
              }
            >
              <Row label='Payer'>{insurance.insurance_name}</Row>
              <Row label='Plan' show={Boolean(insurance.plan_name)}>
                {insurance.plan_name}
              </Row>
              <Row label='Policy' show={Boolean(insurance.policy_number)}>
                {insurance.policy_number}
              </Row>
              <Row label='Group' show={Boolean(insurance.group_number)}>
                {insurance.group_number}
              </Row>
              <Row label='Effective' show={Boolean(insurance.effective_start)}>
                {formatDate(insurance.effective_start)}
                {insurance.effective_end &&
                  ` to ${formatDate(insurance.effective_end)}`}
              </Row>
            </Section>
          ))}

          {episode && (
            <Section title='Episode'>
              <Row label='Start of Care'>
                {socLoading ? (
                  <Skeleton className='h-4 w-24' />
                ) : (
                  (formatDate(socDate) ?? EMPTY)
                )}
              </Row>
              <Row label='Cert Period'>
                {formatDate(episode.start_date)} to{' '}
                {formatDate(episode.end_date)}
              </Row>
              <Row label='Status'>{statusDisplayLabel(episode.status)}</Row>
              <Row label='Case Manager'>
                {episode.case_manager_name || EMPTY}
              </Row>
              <Row label='Clinician'>
                {episode.assigned_clinician_name || EMPTY}
              </Row>
              {frequenciesLoading ? (
                <Row label='Frequency'>
                  <Skeleton className='h-4 w-32' />
                </Row>
              ) : (
                orderedDisciplines.length > 0 && (
                  <>
                    {orderedDisciplines.map(discipline => (
                      <Row key={discipline.key} label={discipline.label}>
                        {currentFrequency?.[discipline.key]}
                      </Row>
                    ))}
                    <Row
                      label='Ordered For'
                      show={Boolean(currentFrequency?.start_date)}
                    >
                      {formatDate(currentFrequency?.start_date)} to{' '}
                      {formatDate(currentFrequency?.end_date)}
                    </Row>
                  </>
                )
              )}
            </Section>
          )}

          {episode && (
            <Section title='Physician'>
              {physicianName ? (
                <>
                  <Row label='Name'>{physicianName}</Row>
                  {physician && (
                    <>
                      <Row label='Phone' show={Boolean(physician.phone)}>
                        <PhoneLink phone={physician.phone} />
                      </Row>
                      {/* Dialable on purpose, matching PhysicianInfoPopover:
                          a physician office's fax line is often answered by a
                          person, and the same number must not be tappable on
                          one screen and dead on another. */}
                      <Row label='Fax' show={Boolean(physician.fax)}>
                        <PhoneLink phone={physician.fax} />
                      </Row>
                      <Row label='NPI' show={Boolean(physician.npi)}>
                        {physician.npi}
                      </Row>
                    </>
                  )}
                </>
              ) : (
                <Row label='Name'>
                  <span className='text-muted-foreground'>
                    No physician assigned
                  </span>
                </Row>
              )}
            </Section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
